import { CartT } from '@lambda/apis/src/cart/types';
import { SkuT } from '@lambda/apis/src/catalog-v2/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RecommendationsByLineId,
  trackRecommendationsViewed,
} from '@/lib/analytics/trackCart';
import { isAccessoryLine } from '../helpers';

type RecommendationsTrackingContextT = {
  reportRecommendations: (lineId: string, recommendations: SkuT[]) => void;
};

const RecommendationsTrackingContext =
  createContext<RecommendationsTrackingContextT | null>(null);

/**
 * Collects the recommendations each line item resolved, then fires a single
 * `Cart Recommendations Viewed` event once every slot on the page has reported.
 *
 * Recommendations are fetched per line item (see `useNewRecommendations`), so
 * without this the page would emit one event per line item. Waiting for all of
 * them keeps the payload aligned with `Cart Viewed`: one event, all products.
 */
export const RecommendationsTrackingProvider = ({
  cart,
  children,
}: {
  cart: CartT | null;
  children: ReactNode;
}) => {
  const [reported, setReported] = useState<RecommendationsByLineId>({});
  const trackedCartId = useRef<string | null>(null);

  const reportRecommendations = useCallback(
    (lineId: string, recommendations: SkuT[]) => {
      setReported((previous) =>
        // First report per line wins — keeps the object identity stable so the
        // effect below does not re-run on every parent render.
        lineId in previous
          ? previous
          : { ...previous, [lineId]: recommendations },
      );
    },
    [],
  );

  useEffect(() => {
    if (!cart) {
      return;
    }

    // One event per cart view. Lines added afterwards (e.g. adding a
    // recommendation to the cart) must not trigger a second event.
    if (trackedCartId.current === cart.id) {
      return;
    }

    const pendingSlots = cart.lines
      .filter((line) => !isAccessoryLine(line))
      .some((line) => !(line.id in reported));

    if (pendingSlots) {
      return;
    }

    trackedCartId.current = cart.id;

    trackRecommendationsViewed(cart, reported);
  }, [cart, reported]);

  const value = useMemo(
    () => ({ reportRecommendations }),
    [reportRecommendations],
  );

  return (
    <RecommendationsTrackingContext.Provider value={value}>
      {children}
    </RecommendationsTrackingContext.Provider>
  );
};

/**
 * Reports the recommendations a line item resolved. No-ops outside the
 * provider so the component stays usable on its own.
 */
export const useReportRecommendations = () => {
  const context = useContext(RecommendationsTrackingContext);

  return context?.reportRecommendations;
};
